import {Link} from 'react-router-dom';
import { isPhotoPlace } from '../services/placeModel';

import React, { useState } from 'react';
import { MapFeature } from '../types';
import { MockAPI } from '../services/api';
import { Button } from '../components/ui';
import { Save, X, AlertCircle, Trash2 } from 'lucide-react';
import MapFeatureEditor from '../components/MapFeatureEditor';
import { SyncService } from '../services/syncService';
import {usePresentationHold} from '../services/usePresentation';
import {usePhotoNavigationGuard} from '../services/photos/usePhotoNavigationGuard';
import {hasPlaceDraftChanges} from '../services/placeDraft';

interface MapFeatureDetailModalProps {
    feature: MapFeature;
    onClose: () => void;
    onSuccess: () => void;
}
const SUPPORTED_MAP_ENTITY_TYPES = new Set(['farm', 'asset', 'project', 'map_feature']);

const MapFeatureDetailModal: React.FC<MapFeatureDetailModalProps> = ({ feature, onClose, onSuccess }) => {
    usePresentationHold(true,'Back to the map');
    const [formData, setFormData] = useState<Partial<MapFeature>>({ ...feature });
    const [isDeleting, setIsDeleting] = useState(false);
    usePhotoNavigationGuard(hasPlaceDraftChanges(formData,feature)||isDeleting);
    
    const [error, setError] = useState<string | null>(null);

    const handleUpdate = (updates: Partial<MapFeature>) => {
        setFormData(prev => ({ ...prev, ...updates }));
    };

    const handleSave = async () => {
        if (!formData.label) {
            setError("Label is required");
            return;
        }

        try {
            // Sync with entity if linked
            if (formData.entity_type === 'farm' && formData.entity_id) {
                const f = await MockAPI.getFarm(formData.entity_id);
                if (f) {
                    f.boundary_geojson = formData.coordinates_geojson!;
                    await MockAPI.saveFarm(f);
                }
            }

            // Always save the MapFeature record itself.
            const featureToSave = { ...formData } as MapFeature;
            if (featureToSave.entity_type && !SUPPORTED_MAP_ENTITY_TYPES.has(featureToSave.entity_type)) {
                featureToSave.entity_type = undefined;
                featureToSave.entity_id = undefined;
            }
            
            // Fix: If this was a "virtual" feature, we must reset the ID to empty string for creation
            // Virtual features usually start with 'virtual-' prefix or similar non-standard IDs
            if (featureToSave.map_feature_id && featureToSave.map_feature_id.startsWith('virtual-')) {
                featureToSave.map_feature_id = '';
            }

            await MockAPI.saveMapFeature(featureToSave);
            void SyncService.requestSync('mutation');
            onSuccess();
        } catch (err) {
            console.error(err);
            setError("Failed to save changes.");
        }
    };

    const handleDelete = async () => {
        if (confirm("Are you sure you want to delete this place? If it represents a Field or Tract, the boundary will be removed.")) {
            setIsDeleting(true);
            try {
                // Pass the original feature prop to ensure we delete the correct record/entity
                await MockAPI.deleteMapFeature(feature);
                void SyncService.requestSync('mutation');
                // Call onSuccess immediately to close modal and refresh
                onSuccess();
            } catch (err) {
                console.error(err);
                setError("Failed to delete feature.");
                setIsDeleting(false);
            }
        }
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold text-gray-900">Edit Feature</h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X className="h-5 w-5"/></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {error && (
                    <div className="bg-red-50 p-3 rounded text-sm text-red-700 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-2" /> {error}
                    </div>
                )}

                <MapFeatureEditor 
                    feature={formData}
                    onChange={handleUpdate}
                    farmId={feature.farm_id}
                    tracts={[]}
                    fields={[]}
                    showLabel={true}
                />

                {isPhotoPlace(feature) && <Link className="inline-flex min-h-11 items-center text-sm underline text-usda-700" to={'/places/'+encodeURIComponent(feature.map_feature_id)}>Open Place photos</Link>}

                <div className="pt-4 border-t border-gray-100">
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea 
                        name="notes" 
                        rows={3} 
                        value={formData.notes || ''} 
                        onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-usda-500 focus:ring-usda-500 sm:text-sm p-2 border"
                    />
                </div>
            </div>

            <div className="p-4 border-t bg-gray-50 flex justify-between">
                <div className="flex items-center">
                     <Button variant="danger" onClick={handleDelete} className="px-3" title="Delete or Clear Boundary" disabled={isDeleting}>
                        {isDeleting ? '...' : <Trash2 className="h-4 w-4" />}
                     </Button>
                </div>
                
                <div className="flex space-x-3">
                    <Button variant="outline" onClick={onClose} disabled={isDeleting}>Cancel</Button>
                    <Button onClick={handleSave} disabled={isDeleting}>
                        <Save className="h-4 w-4 mr-2" /> Save Changes
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MapFeatureDetailModal;
